'use client';

import GlassButton from '@/components/GlassButton';
import GlassCard from '@/components/GlassCard';
import LiquidGlassContainer from '@/components/LiquidGlassContainer';

export default function GlassDemoPage() {
  return (
    <div className="min-h-screen p-8 space-y-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl tabular-nums tracking-tight font-semibold font-bold text-center mb-8 bg-gradient-to-r from-white to-stone-900 bg-clip-text text-transparent">
          Glass Morphism & Glow Effects Demo
        </h1>

        {/* Glass Buttons Section */}
        <section className="mb-12">
          <h2 className="text-2xl tabular-nums tracking-tight font-semibold mb-6 text-white">Glass Buttons with Glow Effects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            <GlassCard variant="default" className="p-6">
              <h3 className="text-lg font-medium mb-4">Primary Button</h3>
              <GlassButton
                variant="primary"
                size="lg"
                glowMode="pulse"
              >
                Primary Action
              </GlassButton>
            </GlassCard>

            <GlassCard variant="elevated" className="p-6">
              <h3 className="text-lg font-medium mb-4">Success Button</h3>
              <GlassButton
                variant="success"
                size="lg"
                glowMode="rotate"
              >
                Success Action
              </GlassButton>
            </GlassCard>

            <GlassCard variant="gradient" className="p-6">
              <h3 className="text-lg font-medium mb-4">Danger Button</h3>
              <GlassButton
                variant="danger"
                size="lg"
                glowMode="breathe"
              >
                Danger Action
              </GlassButton>
            </GlassCard>

            <GlassCard variant="subtle" className="p-6">
              <h3 className="text-lg font-medium mb-4">Color Shift Glow</h3>
              <GlassButton
                variant="secondary"
                size="lg"
                glowMode="colorShift"
              >
                Color Shift
              </GlassButton>
            </GlassCard>

            <GlassCard variant="default" className="p-6">
              <h3 className="text-lg font-medium mb-4">Flow Horizontal</h3>
              <GlassButton
                variant="ghost"
                size="lg"
                glowMode="flowHorizontal"
              >
                Flow Effect
              </GlassButton>
            </GlassCard>

            <GlassCard variant="elevated" className="p-6">
              <h3 className="text-lg font-medium mb-4">No Glow Effect</h3>
              <GlassButton
                variant="primary"
                size="lg"
                glowEffect={false}
              >
                No Glow
              </GlassButton>
            </GlassCard>
          </div>
        </section>

        {/* Glass Cards Section */}
        <section className="mb-12">
          <h2 className="text-2xl tabular-nums tracking-tight font-semibold mb-6 text-white">Glass Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            <GlassCard variant="default" className="p-6">
              <h3 className="text-xl font-semibold mb-3">Default Glass Card</h3>
              <p className="text-gray-300 mb-4">
                This is a default glass card with subtle glass morphism effects and hover animations.
              </p>
              <GlassButton variant="primary" size="sm">
                Learn More
              </GlassButton>
            </GlassCard>

            <GlassCard variant="elevated" className="p-6">
              <h3 className="text-xl font-semibold mb-3">Elevated Glass Card</h3>
              <p className="text-gray-300 mb-4">
                An elevated glass card with stronger shadows and more pronounced glass effects.
              </p>
              <GlassButton variant="success" size="sm">
                Get Started
              </GlassButton>
            </GlassCard>

            <GlassCard variant="gradient" className="p-6">
              <h3 className="text-xl font-semibold mb-3">Gradient Glass Card</h3>
              <p className="text-gray-300 mb-4">
                A gradient glass card with flowing background gradients and enhanced visual appeal.
              </p>
              <GlassButton variant="danger" size="sm">
                Explore
              </GlassButton>
            </GlassCard>
          </div>
        </section>

        {/* Liquid Glass Containers Section */}
        <section className="mb-12">
          <h2 className="text-2xl tabular-nums tracking-tight font-semibold mb-6 text-white">Liquid Glass Containers</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            <LiquidGlassContainer variant="flowing" intensity="medium">
              <h3 className="text-xl font-semibold mb-4">Flowing Liquid Glass</h3>
              <p className="text-gray-300 mb-4">
                This container features flowing liquid glass animations with smooth gradient transitions
                that create a mesmerizing effect. Perfect for showcasing premium content or features.
              </p>
              <div className="flex gap-3">
                <GlassButton variant="primary" size="sm">
                  Primary
                </GlassButton>
                <GlassButton variant="ghost" size="sm">
                  Secondary
                </GlassButton>
              </div>
            </LiquidGlassContainer>

            <LiquidGlassContainer variant="ripple" intensity="strong">
              <h3 className="text-xl font-semibold mb-4">Ripple Effect</h3>
              <p className="text-gray-300 mb-4">
                Experience the ripple effect with radial gradient animations that emanate from different
                points across the container, creating a dynamic and engaging visual experience.
              </p>
              <div className="flex gap-3">
                <GlassButton variant="success" size="sm">
                  Success
                </GlassButton>
                <GlassButton variant="secondary" size="sm">
                  Info
                </GlassButton>
              </div>
            </LiquidGlassContainer>

            <LiquidGlassContainer variant="wave" intensity="subtle">
              <h3 className="text-xl font-semibold mb-4">Wave Animation</h3>
              <p className="text-gray-300 mb-4">
                Subtle wave animations that flow across the container in different directions,
                providing a gentle and elegant visual enhancement without being distracting.
              </p>
              <div className="flex gap-3">
                <GlassButton variant="danger" size="sm">
                  Warning
                </GlassButton>
                <GlassButton variant="ghost" size="sm">
                  Neutral
                </GlassButton>
              </div>
            </LiquidGlassContainer>

            <LiquidGlassContainer variant="default" intensity="medium">
              <h3 className="text-xl font-semibold mb-4">Static Liquid Glass</h3>
              <p className="text-gray-300 mb-4">
                A clean static liquid glass container with beautiful glass morphism effects,
                perfect for content that needs to be readable without distracting animations.
              </p>
              <div className="flex gap-3">
                <GlassButton variant="primary" size="sm" glowMode="pulse">
                  Animated
                </GlassButton>
                <GlassButton variant="ghost" size="sm" glowEffect={false}>
                  Static
                </GlassButton>
              </div>
            </LiquidGlassContainer>
          </div>
        </section>

        {/* Interactive Demo Section */}
        <section className="mb-12">
          <h2 className="text-2xl tabular-nums tracking-tight font-semibold mb-6 text-white">Interactive Demo</h2>
          <LiquidGlassContainer variant="flowing" intensity="strong" className="p-8">
            <div className="text-center">
              <h3 className="text-2xl tabular-nums tracking-tight font-semibold font-bold mb-4 bg-gradient-to-r from-stone-800 to-stone-900 bg-clip-text text-transparent">
                Try the Effects!
              </h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Hover over the buttons below to see different glow effects and glass morphism in action.
                Each button demonstrates a different animation mode and color scheme.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <GlassButton
                  variant="primary"
                  glowMode="rotate"
                >
                  Rotating Glow
                </GlassButton>
                <GlassButton
                  variant="success"
                  glowMode="pulse"
                >
                  Pulsing Glow
                </GlassButton>
                <GlassButton
                  variant="danger"
                  glowMode="breathe"
                >
                  Breathing Glow
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  glowMode="colorShift"
                >
                  Color Shifting
                </GlassButton>
              </div>
            </div>
          </LiquidGlassContainer>
        </section>
      </div>
    </div>
  );
}
